import React, { useState } from "react";
import { Button, Input, message } from "antd";
import {
  FileProtectOutlined, SyncOutlined,
  CheckCircleFilled, WarningFilled, CloseCircleFilled,
} from "@ant-design/icons";
import { updateLegalName } from "../../../services/vendorMaster";
import { useThemeStore } from "../../../store/useThemeStore";

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

export const UpdateLegalName = () => {
  const { themeColor } = useThemeStore();
  const rgb = hexToRgb(themeColor);

  const [guid,    setGuid]    = useState("");
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null); // { type: 'success'|'warning'|'error', message: string }
  const [error,   setError]   = useState("");

  function handleChange(val) {
    setGuid(val.toUpperCase());
    if (error) setError("");
    if (result) setResult(null);
  }

  async function handleSave() {
    if (!guid.trim()) { setError("Vendor GUID is required"); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await updateLegalName(guid.trim());
      if (res?.Data) {
        setResult({ type: "success", message: res.DisplayMessage || "Legal name updated successfully." });
        message.success(res.DisplayMessage || "Legal name updated successfully.");
        setGuid("");
      } else {
        setResult({ type: "warning", message: res?.DisplayMessage || "No changes were made." });
        message.warning(res?.DisplayMessage || "No changes were made.");
      }
    } catch {
      setResult({ type: "error", message: "Failed to update legal name. Please try again." });
      message.error("Failed to update legal name. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const resultConfig = {
    success: { icon: <CheckCircleFilled style={{ fontSize: 18, color: "#16a34a" }} />, bg: "#f0fdf4", border: "#bbf7d0", color: "#15803d" },
    warning: { icon: <WarningFilled     style={{ fontSize: 18, color: "#d97706" }} />, bg: "#fffbeb", border: "#fde68a", color: "#92400e" },
    error:   { icon: <CloseCircleFilled style={{ fontSize: 18, color: "#dc2626" }} />, bg: "#fff1f2", border: "#fecaca", color: "#991b1b" },
  };

  return (
    <div style={{ padding: "4px 0", display: "flex", flexDirection: "column", gap: 10 }}>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 14, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "12px 18px",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: themeColor,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 4px 14px ${themeColor}44`, transition: "background 0.3s",
        }}>
          <FileProtectOutlined style={{ fontSize: 17, color: "#fff" }} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Update Legal Name</h2>
          <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>Sync vendor legal name from SAP using the vendor GUID</p>
        </div>
      </div>

      {/* ── Action card ──────────────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "28px 24px",
        maxWidth: 560,
      }}>

        {/* Info banner */}
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 12,
          padding: "12px 14px", borderRadius: 10, marginBottom: 22,
          background: `rgba(${rgb},0.06)`,
          border: `1px solid rgba(${rgb},0.18)`,
        }}>
          <FileProtectOutlined style={{ fontSize: 18, color: themeColor, marginTop: 1, flexShrink: 0 }} />
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>SAP Legal Name Sync</p>
            <p style={{ margin: 0, fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
              Enter the vendor's GUID to pull the latest legal name from SAP and update it in the system.
            </p>
          </div>
        </div>

        {/* Input */}
        <div style={{ marginBottom: 6 }}>
          <label style={{
            fontSize: 11, fontWeight: 700, color: "#374151",
            textTransform: "uppercase", letterSpacing: "0.07em",
            display: "block", marginBottom: 6,
          }}>
            Vendor GUID <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <Input
              value={guid}
              onChange={e => handleChange(e.target.value)}
              onPressEnter={handleSave}
              placeholder="Enter Vendor GUID (e.g. ABC123-XYZ)"
              status={error ? "error" : ""}
              style={{
                borderRadius: 9, height: 42, fontFamily: "monospace",
                fontSize: 13, fontWeight: 600, letterSpacing: "0.04em",
                flex: 1,
              }}
              allowClear
              prefix={
                <FileProtectOutlined style={{ color: "#c4c7cd", fontSize: 13 }} />
              }
            />
            <Button
              onClick={handleSave}
              loading={loading}
              icon={loading ? <SyncOutlined spin /> : <FileProtectOutlined />}
              style={{
                height: 42, borderRadius: 9, fontWeight: 700, fontSize: 13,
                background: guid.trim() ? themeColor : undefined,
                borderColor: guid.trim() ? themeColor : undefined,
                color: guid.trim() ? "#fff" : undefined,
                boxShadow: guid.trim() ? `0 2px 8px ${themeColor}44` : undefined,
                paddingLeft: 20, paddingRight: 20,
                transition: "all 0.3s",
              }}
            >
              {loading ? "Updating…" : "Update"}
            </Button>
          </div>
          {error && (
            <p style={{ margin: "5px 0 0", fontSize: 12, color: "#dc2626", display: "flex", alignItems: "center", gap: 4 }}>
              <CloseCircleFilled style={{ fontSize: 11 }} /> {error}
            </p>
          )}
        </div>

        <p style={{ margin: "8px 0 0", fontSize: 11, color: "#c4c7cd" }}>
          GUID is automatically uppercased · Press Enter to submit
        </p>

        {/* Result card */}
        {result && (() => {
          const cfg = resultConfig[result.type];
          return (
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 16px", borderRadius: 10, marginTop: 18,
              background: cfg.bg, border: `1px solid ${cfg.border}`,
              animation: "fadeIn 0.25s ease",
            }}>
              {cfg.icon}
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: cfg.color }}>{result.message}</p>
            </div>
          );
        })()}
      </div>

    </div>
  );
};

export default UpdateLegalName;
